/**
 * Where v0.28 counts the surfaced step's changes: on the rail, and on the
 * Novelty page's tabs. Only the surfaced step is counted; later steps are held
 * until it is reviewed (lane M decision 21).
 */

import type {
  NotificationStep,
  NotificationsResponse,
} from "@patent-studio/api-types";
import type { RailCounts } from "@/v2/layout/StageRail";
import {
  stagePath,
  subStepPath,
  tabPath,
  type TabId,
} from "@/v2/stage-vocabulary";

/**
 * Each step in v0.28's order: what a pointer calls it, the trail the review
 * card names it by, and the page its changes are reviewed on.
 */
export const STEP_PLACES: Record<
  NotificationStep,
  { name: string; label: string; to: (taskId: string) => string }
> = {
  invention: {
    name: "Invention",
    label: "Thesis / Invention",
    to: (taskId) => subStepPath(taskId, "thesis", "invention"),
  },
  define_positions: {
    name: "Define Positions",
    label: "Thesis / Novelty / Define Positions",
    to: (taskId) => tabPath(taskId, "thesis", "novelty", "positions"),
  },
  novelty_analysis: {
    name: "Novelty Analysis",
    label: "Thesis / Novelty / Novelty Analysis",
    to: (taskId) => tabPath(taskId, "thesis", "novelty", "analysis"),
  },
  claim_strategy: {
    name: "Claim Strategy",
    label: "Claim Strategy",
    to: (taskId) => stagePath(taskId, "claim-strategy"),
  },
  specification: {
    name: "Specification",
    label: "Specification",
    to: (taskId) => stagePath(taskId, "planning-outline"),
  },
};

const STEP_ORDER = Object.keys(STEP_PLACES) as NotificationStep[];

/** Whether one step comes before another, so its changes are reviewed first. */
export function comesBefore(
  step: NotificationStep,
  other: NotificationStep,
): boolean {
  return STEP_ORDER.indexOf(step) < STEP_ORDER.indexOf(other);
}

function surfacedCount(view: NotificationsResponse | undefined): number {
  return view?.steps.find((s) => s.step === view.surfaced)?.pending ?? 0;
}

/** The rail's counts: the step's own row, and its stage's row for when the steps are folded. */
export function railCounts(
  view: NotificationsResponse | undefined,
): RailCounts {
  const count = surfacedCount(view);
  switch (view?.surfaced) {
    case "invention":
      return { thesis: count, invention: count };
    case "define_positions":
    case "novelty_analysis":
      return { thesis: count, novelty: count };
    case "claim_strategy":
      return { "claim-strategy": count };
    default:
      return {};
  }
}

/** The Novelty page's tab counts: Define Positions for a position changed, Novelty Analysis for one added. */
export function noveltyTabCounts(
  view: NotificationsResponse | undefined,
): Partial<Record<TabId, number>> {
  const count = surfacedCount(view);
  if (view?.surfaced === "define_positions") return { positions: count };
  if (view?.surfaced === "novelty_analysis") return { analysis: count };
  return {};
}
